import { useNavigate } from "react-router-dom";
import { Plus, Zap } from "lucide-react";
import { useInvoices } from "@/hooks/useInvoices";
import { useCompanies } from "@/hooks/useCompanies";
import { useUrlState } from "@/hooks/useUrlState";
import { InvoiceStatusBadge } from "@/components/ui/invoice-status-badge";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/ui/data-table";
import { SearchInput } from "@/components/ui/search-input";
import { Pagination } from "@/components/ui/pagination";
import { Button } from "@/components/ui/button";
import { ListSummary } from "@/components/ui/list-summary";
import { ComboboxSelect } from "@/components/form/combobox-select";
import { isQuickReceiptInvoice } from "@/lib/invoice-source";
import type { Invoice } from "@/types/invoice";
import type { Column } from "@/components/ui/data-table";

const STATUS_FILTERS = [
  { value: "", label: "Todos" },
  { value: "PENDING", label: "Pendente" },
  { value: "OVERDUE", label: "Vencida" },
  { value: "PARTIAL", label: "Parcial" },
  { value: "PAID", label: "Pago" },
];

function formatMoney(value: string) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number.parseFloat(value));
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("pt-BR");
}

const COLUMNS: Column<Invoice>[] = [
  {
    key: "invoiceNumber",
    header: "Nº Fatura",
    render: (row) => (
      <span className="inline-flex items-center gap-1.5">
        <span className="font-medium text-foreground">{row.invoiceNumber}</span>
        {isQuickReceiptInvoice(row.invoiceNumber) && (
          <Badge tone="info" icon={<Zap size={10} />}>via atalho</Badge>
        )}
      </span>
    ),
  },
  {
    key: "company",
    header: "Empresa",
    render: (row) => row.company.tradeName,
  },
  {
    key: "totalAmount",
    header: "Total",
    render: (row) => formatMoney(row.totalAmount),
  },
  {
    key: "issueDate",
    header: "Emissão",
    className: "hidden lg:table-cell",
    render: (row) => formatDate(row.issueDate),
  },
  {
    key: "dueDate",
    header: "Vencimento",
    render: (row) => (
      <span className={row.isOverdue ? "text-red-600 font-medium" : ""}>
        {formatDate(row.dueDate)}
      </span>
    ),
  },
  {
    key: "status",
    header: "Status",
    render: (row) => (
      <InvoiceStatusBadge status={row.status} isOverdue={row.isOverdue} />
    ),
  },
];

export function InvoiceListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useUrlState<string>("search", "");
  const [statusFilter, setStatusFilter] = useUrlState<string>("status", "");
  const [companyFilter, setCompanyFilter] = useUrlState<string>("companyId", "");
  const [page, setPage] = useUrlState<number>("page", 1);

  const { data: companiesData } = useCompanies({ active: true, perPage: 200 });
  const companyOptions = [
    { value: "", label: "Todas as empresas" },
    ...(companiesData?.data ?? []).map((c) => ({
      value: c.id,
      label: c.tradeName,
    })),
  ];

  const { data, isLoading } = useInvoices({
    search,
    status: statusFilter || undefined,
    companyId: companyFilter || undefined,
    page,
  });

  function handleSearch(v: string) {
    setSearch(v);
    setPage(1);
  }

  function handleStatus(v: string) {
    setStatusFilter(v);
    setPage(1);
  }

  function handleCompany(v: string) {
    setCompanyFilter(v);
    setPage(1);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Faturas</h1>
          <p className="text-sm text-muted-foreground">
            Gerenciamento de faturas e recebimentos
          </p>
        </div>
        <Button onClick={() => navigate("/invoices/new")}>
          <Plus size={16} />
          Nova fatura
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchInput
          value={search}
          onChange={handleSearch}
          placeholder="Buscar por número de fatura ou empresa…"
          className="flex-1"
        />
        <ComboboxSelect
          options={companyOptions}
          value={companyFilter}
          onChange={handleCompany}
          placeholder="Todas as empresas"
          searchPlaceholder="Buscar empresa..."
          className="w-full sm:w-52"
        />
      </div>

      <div className="flex overflow-hidden rounded-md border border-input text-sm w-fit">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => handleStatus(f.value)}
            className={`px-3 py-1.5 transition-colors whitespace-nowrap ${
              statusFilter === f.value
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-muted/50"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {data && (
        <ListSummary
          metrics={[
            {
              label: "Total faturado",
              value: formatMoney(data.totals.totalAmount),
              tone: "neutral",
              hint: `${data.total} ${data.total === 1 ? "fatura" : "faturas"}`,
            },
            {
              label: "Recebido",
              value: formatMoney(data.totals.paidAmount),
              tone: "positive",
            },
            {
              label: "A receber",
              value: formatMoney(data.totals.openAmount),
              tone:
                Number.parseFloat(data.totals.openAmount) > 0 ? "warning" : "neutral",
            },
          ]}
        />
      )}

      <DataTable
        columns={COLUMNS}
        data={data?.data ?? []}
        keyExtractor={(row) => row.id}
        onRowClick={(row) => navigate(`/invoices/${row.id}`)}
        isLoading={isLoading}
        emptyTitle="Nenhuma fatura encontrada"
        emptyDescription="Comece cadastrando a primeira fatura."
        emptyAction={
          <Button onClick={() => navigate("/invoices/new")}>
            <Plus size={16} />
            Nova fatura
          </Button>
        }
      />

      {data && (
        <Pagination
          page={data.page}
          totalPages={data.totalPages}
          total={data.total}
          perPage={data.perPage}
          onChange={setPage}
        />
      )}
    </div>
  );
}
